// Per-agent delivery metadata cache for the manager.
//
// `agentInfoForDelivery` is populated from each /host/refresh payload by
// cacheAgentDeliveryMetadata (called from the refresh path in manager-worker)
// and read by the scheduled-output delivery orchestrator. Holding it in a
// dependency-free leaf lets both the writer (refresh) and the reader
// (delivery/scheduled-output) share one singleton without a cycle — the same
// pattern as channels/state.ts and agent-state.ts. See ADR-0011.

import type { ResolverAgent, ResolverPerson } from '@augmented/core';
import { DEFAULT_FRAMEWORK } from '@augmented/core';

/** Per-agent metadata the delivery resolver needs. Populated during the
 *  refresh cycle from the host-runtime payload (see §8). */
export interface AgentDeliveryMetadata {
  agentDisplayName: string;
  ownerTeamName: string | null;
  resolverAgent: ResolverAgent;
  peopleByPersonId: Map<string, ResolverPerson>;
}
export const agentInfoForDelivery = new Map<string, AgentDeliveryMetadata>();

/** Build `AgentDeliveryMetadata` from a refresh payload. Called whenever the
 *  manager-worker syncs an agent so the resolver sees the latest reports_to /
 *  owner_team / people-map state. */
export function cacheAgentDeliveryMetadata(codeName: string, refreshData: Record<string, unknown>): void {
  const agentRow = (refreshData['agent'] ?? {}) as Record<string, unknown>;
  const displayName = (agentRow['display_name'] as string | undefined) ?? codeName;
  const ownerTeamName = (agentRow['owner_team_name'] as string | null | undefined) ?? null;
  const framework = (agentRow['framework'] as string | undefined) ?? DEFAULT_FRAMEWORK;
  const reportsToPersonId = (agentRow['reports_to'] as string | null | undefined) ?? null;
  const reportsToType = (agentRow['reports_to_type'] as 'person' | 'agent' | null | undefined) ?? null;

  // dm_capable_mediums: mirror the *same* "active + credentialed" predicate
  // the token cache uses (CR #3108333177). A channel config without a
  // bot_token isn't really DM-capable — with medium: 'auto', a pending or
  // broken Slack install would otherwise beat a healthy Telegram one.
  const channelConfigs = (refreshData['channel_configs'] ?? {}) as Record<string, { config?: Record<string, unknown> } | undefined>;
  const dmCapable: Array<'slack' | 'telegram'> = [];
  const slackBotToken = channelConfigs['slack']?.config?.['bot_token'];
  if (typeof slackBotToken === 'string' && slackBotToken.length > 0) {
    dmCapable.push('slack');
  }
  const telegramBotToken = channelConfigs['telegram']?.config?.['bot_token'];
  if (typeof telegramBotToken === 'string' && telegramBotToken.length > 0) {
    dmCapable.push('telegram');
  }

  const resolverAgent: ResolverAgent = {
    agent_id: (agentRow['agent_id'] as string) ?? '',
    framework,
    dm_capable_mediums: dmCapable,
    reports_to_person_id: reportsToType === 'person' ? reportsToPersonId : null,
    reports_to_type: reportsToType,
  };

  const peopleByPersonId = new Map<string, ResolverPerson>();
  // The reports_to person is carried directly on the agent row with its DM ids.
  if (reportsToPersonId && reportsToType === 'person') {
    peopleByPersonId.set(reportsToPersonId, {
      person_id: reportsToPersonId,
      display_name: (agentRow['reports_to_name'] as string | undefined) ?? 'Reports-To',
      slack_user_id: (agentRow['reports_to_slack_user_id'] as string | null | undefined) ?? null,
      telegram_chat_id: (agentRow['reports_to_telegram_chat_id'] as string | null | undefined) ?? null,
    });
  }
  // Other org people (for pinned dm:person:<id> targets) come via
  // refreshData.people. Keys may not be by person_id — guard and skip rows
  // without a person_id field.
  const people = (refreshData['people'] ?? []) as Array<Record<string, unknown>>;
  for (const p of people) {
    const personId = p['person_id'] as string | undefined;
    if (!personId) continue;
    const contactPrefs = (p['contact_preferences'] ?? {}) as Record<string, unknown>;
    peopleByPersonId.set(personId, {
      person_id: personId,
      display_name: (p['display_name'] as string | undefined) ?? 'person',
      slack_user_id: (contactPrefs['slack_user_id'] as string | null | undefined) ?? null,
      telegram_chat_id: (contactPrefs['telegram_chat_id'] as string | null | undefined) ?? null,
    });
  }

  agentInfoForDelivery.set(codeName, {
    agentDisplayName: displayName,
    ownerTeamName,
    resolverAgent,
    peopleByPersonId,
  });
}
